import React, { useMemo, useRef } from 'react';
import { ApolloProvider, ApolloClient, createHttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import errorTracker from '@services/error-tracking';
import configuration from '@config';

import { useAuth } from '@auth-wrapper';

import { isPublicProposalPath, resolveRequestToken } from './request-token';

interface ApolloWrapperProps {
  children: React.ReactNode;
}

function isPublicProposalRoute(): boolean {
  return isPublicProposalPath(window.location.pathname);
}

const ApolloWrapper: React.FC<ApolloWrapperProps> = ({ children }) => {
  const { token: sessionToken } = useAuth();

  const sessionTokenRef = useRef(sessionToken);
  sessionTokenRef.current = sessionToken;

  const httpLink = createHttpLink({
    uri: configuration.GATEWAY_API_HOST,
  });

  const authLink = useMemo(
    () =>
      setContext((_, { headers, ...rest }) => {
        const currentToken = resolveRequestToken(sessionTokenRef.current, window.location);
        if (!currentToken) return { headers, ...rest };
        return {
          ...rest,
          headers: {
            ...headers,
            Authorization: `Bearer ${currentToken}`,
          },
        };
      }),
    [],
  );

  const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
    const { variables, operationName } = operation;
    const context = { name: operationName, values: variables };

    if (networkError) {
      // Handle token expiration — skip redirect on public proposal routes (Puppeteer access)
      if ('statusCode' in networkError && networkError.statusCode === 401) {
        if (!isPublicProposalRoute()) {
          const url = new URL(import.meta.url);
          const protocol = url.protocol;
          const host = url.host;
          const currentDomain = `${protocol}//${host}`;
          const serverPort = configuration.SERVER_PORT ?? '5001';
          const serverBaseURL = `${currentDomain.replace(
            /:\d+/,
            `${configuration.IS_IN_PRODUCTION === true ? '' : `:${serverPort}`}`,
          )}/api/auth`;
          window.location.href = `${serverBaseURL}/login`;
        }
        return;
      }

      context.values = {
        ...context.values,
        errorType: 'network',
        rawErrorObject: JSON.stringify(networkError),
        rawResponseObject: JSON.stringify(response),
        rawVariables: JSON.stringify(variables),
      };
      errorTracker.captureException(networkError, context);
    }

    if (graphQLErrors) {
      graphQLErrors.forEach((graphQLError) => {
        const err = new Error(`GraphQL error on ${operationName}`, { cause: graphQLError });
        context.values = {
          ...context.values,
          errorType: 'graphql',
          rawErrorObject: JSON.stringify(graphQLError),
        };
        errorTracker.captureException(err, context);
      });
    }
  });

  const client = useMemo(
    () =>
      new ApolloClient({
        link: from([errorLink, authLink.concat(httpLink)]),
        cache: new InMemoryCache(),
      }),
    [sessionToken],
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloWrapper;
